import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { HTTPStatusCode } from 'config/const';
import { ApiQueryOperator, IApiQueryParam, IPayPeriod, IPayPeriodClosure, PayPeriodStatus } from 'types';
import { formatDateForUI } from './date';

// An open period whose end date is this many calendar days past, in the site's timezone, gets the Recommend Closing banner.
const RECOMMEND_CLOSING_AFTER_DAYS = 3;

export enum PayPeriodBannerState {
  OPEN = 'open',
  IN_PROGRESS = 'inProgress',
  RECOMMEND_CLOSING = 'recommendClosing',
  CLOSED = 'closed',
}

export const isPayPeriodClosed = (payPeriod?: IPayPeriod): boolean => payPeriod?.status === PayPeriodStatus.CLOSED;

// A period is identified by its range: a site's schedule yields exactly one period per range.
export const payPeriodKey = (payPeriod: IPayPeriod) => `${payPeriod.start}-${payPeriod.end}`;

export const formatPayPeriodLabel = (payPeriod: IPayPeriod) => `${formatDateForUI(payPeriod.start)} - ${formatDateForUI(payPeriod.end)}`;

// Only a close that converted payouts to payroll changes the period's stats. A failed close comes back without a closure.
export const hasCloseChangedStats = (closure?: IPayPeriodClosure) => !isEqual(closure?.stats.atClose, closure?.stats.afterConversion);

// The period holding a YYYY-MM-DD date.
export const findPayPeriod = (payPeriods: IPayPeriod[], date?: string) => (date ? payPeriods.find((payPeriod) => payPeriod.start <= date && date <= payPeriod.end) : undefined);

// The period was already closed or reopened (409), or never closed (404).
export const isStalePayPeriodError = (error?: { statusCode?: string }) => [HTTPStatusCode.CONFLICT, HTTPStatusCode.NOT_FOUND].includes(error?.statusCode as HTTPStatusCode);

// The query filters for payouts or payment sets dated inside the period.
export const payPeriodDateFilters = (payPeriod: IPayPeriod): IApiQueryParam[] => [
  { key: 'payment_date', operator: ApiQueryOperator.gte, value: payPeriod.start },
  { key: 'payment_date', operator: ApiQueryOperator.lte, value: payPeriod.end },
];

/**
 * Which banner the Pay Period tab shows for a period. Reopened periods read as open: they are writable and
 * offer Close, exactly like a period with no closure record.
 *
 * @param today - YYYY-MM-DD in the site's timezone.
 */
export const getPayPeriodBannerState = (payPeriod: IPayPeriod, today: string): PayPeriodBannerState => {
  if (isPayPeriodClosed(payPeriod)) return PayPeriodBannerState.CLOSED;
  if (!dayjs(payPeriod.end).isBefore(today, 'day')) return PayPeriodBannerState.IN_PROGRESS;
  if (dayjs(today).diff(payPeriod.end, 'day') >= RECOMMEND_CLOSING_AFTER_DAYS) return PayPeriodBannerState.RECOMMEND_CLOSING;
  return PayPeriodBannerState.OPEN;
};
